import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import type { OrpcClient } from '@/orpc/client'
import { useOrpc } from './useClient'

export type SetPractitionerStediProviderInput = Parameters<
  OrpcClient['setPractitionerStediProvider']
>[0]

/**
 * Links or unlinks a member and a Stedi provider record. Without the link Stedi's
 * enrollment state and remittances name nobody we recognise, so the notifications say
 * what the mapping enables rather than reporting a generic save.
 *
 * With a live key the middleware checks the record against Stedi — including that its NPI
 * matches this member's — before persisting; its rejection messages are written to be
 * shown as-is. `verified: false` is worth surfacing but is not a failure: it only happens
 * under a test-mode key, where Stedi's enrollments product refuses to answer at all, and
 * the mapping gets verified naturally once the organization goes live.
 */
export function useSetPractitionerStediProvider() {
  const orpc = useOrpc()

  return useMutation(
    orpc.setPractitionerStediProvider.mutationOptions({
      onSuccess: (result) => {
        if (!result.stediProviderId) {
          showNotification({
            color: 'green',
            title: 'Stedi provider unlinked',
            message: 'This member is no longer mapped to a Stedi provider record.',
          })
          return
        }
        showNotification({
          color: result.verified ? 'green' : 'yellow',
          title: 'Stedi provider linked',
          message: result.verified
            ? `Mapped to Stedi provider ${result.stediProviderId}.`
            : `Mapped to Stedi provider ${result.stediProviderId}. Stedi refuses provider reads in test mode, so the id was saved unverified — it is checked for real once this organization runs on a live key.`,
        })
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          title: 'Stedi mapping not saved',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
    }),
  )
}
